import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Button,
  useMediaQuery,
  useTheme,
  Typography,
  Card,
  Collapse,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import applicationDetailsServices from "../../services/api/service/applicationDetails";
import losServices from "../../services/api/service/losService";
import dropdownService from "../../services/api/service/dropdownService";
import { tabContentSkeleton } from "../../pages/summary/ApplicationDetails";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../services/redux/store";
import {
  setCoApplicantAddressDetails,
  setCoApplicantBasicDetails,
  setCoApplicantEmploymentDetails,
} from "../../services/redux/slices/applicationLoanDetailsSlice";
import {
  requestSummaryFormSave,
  setPartnerStartedJourney,
  setSummaryApplicationEditActive,
  setSummaryEditingCoApplicantKey,
  setSummarySaveInProgress,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { useSelector as useReduxSelector } from "react-redux";
import { RootState as ReduxRootState } from "../../services/redux/store";
import { formatDOB,mergeMissingCoApplicants } from "../../utils/helpers";
import DetailRow from "../../pages/common-features/DetailRow";
import FormEdit from "../ui/StepperDyanmicForm/FormEdit";
import RemoveIcon from "./../../assets/svgIcons/remove-icon.svg";
import { useApplicantMasterData } from "../../hooks/useApplicantMasterData";
import EditIcon from "@mui/icons-material/Edit";
import ConsentAuthorizationButton from "../../components/common/ConsentAuthorizationButton";
import {
  getCompanyAddressLabel,
  isCompanyAddressField,
  isCompanyNameField,
  shouldShowCompanyAddress,
  sortCompanyAddressFields,
} from "./employmentSummaryHelper";
interface SingleCoApplicantTabsProps {
  keyNumber: number;
  coApplicantFields: any[];
  coApplicantId: number | string;
  coApplicantNumber: number;
  title: string;
  isExpanded: boolean;
  onRemoveCoApplicant?: (coApplicantNumber: number) => Promise<void> | void;
  relationDescByCode?: Record<string, string>;
  maritalStatusDescByCode?: Record<string, string>;
  resolveBirthCountryName?: (rawCountryValue: any) => string;
  getCityOptionsForCountry?: (countryName: string) => { value: string; label: string }[];
  designationsVal?: any;
}

const isDesignationField = (field: any): boolean =>
  field?.name === "designation" ||
  /designation$/i.test(String(field?.name || "")) ||
  String(field?.label || "").toLowerCase() === "designation";

// Fixed display order for the Co-Applicant Basic Details tab — independent
// of whatever order the saved-values fetch or the field config happens to
// return fields in.
const CO_APPLICANT_BASIC_DISPLAY_ORDER = [
  "relationwithstudent",
  "salutation",
  "firstname",
  "lastname",
  "gender",
  "dob",
  "pannumber",
  "phonenumber",
  "email",
  "nationality",
  "maritalstatus",
  "countryofbirth",
  "cityofbirth",
  "payparty",
];

// mapCoApplicantDetailsFromVerticalSteps (used for the !partnerStartedJourney
// data source) renames FirstName/LastName's `.name` to a single shared
// "coApplicant<N>Name" for both fields, and similarly folds
// RelationWithStudent/Email/Phone_number's names — so a name-derived key
// alone can't tell First Name from Last Name there. Its `.label` stays
// distinct in every case, so fall back to matching on that (normalized)
// whenever the name-derived key doesn't hit the canonical list directly.
const CO_APPLICANT_BASIC_LABEL_ALIASES: Record<string, string> = {
  relationshipwithstudent: "relationwithstudent",
  coapplicantfirstname: "firstname",
  coapplicantlastname: "lastname",
  mobilenumber: "phonenumber",
  emailid: "email",
};

const normalizeCoApplicantOrderToken = (raw: any): string =>
  String(raw || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const getCoApplicantBasicFieldOrderKey = (field: any): string => {
  // Strip the "basicCoApplicant<N>" / "coApplicant<N>" prefix, leaving just
  // the suffix to match against the canonical order above.
  const nameKey = normalizeCoApplicantOrderToken(
    field?.name || field?.key || "",
  ).replace(/^(basiccoapplicant|coapplicant)\d+/, "");
  if (CO_APPLICANT_BASIC_DISPLAY_ORDER.includes(nameKey)) return nameKey;

  const labelKey = normalizeCoApplicantOrderToken(field?.label);
  if (CO_APPLICANT_BASIC_LABEL_ALIASES[labelKey]) {
    return CO_APPLICANT_BASIC_LABEL_ALIASES[labelKey];
  }
  if (CO_APPLICANT_BASIC_DISPLAY_ORDER.includes(labelKey)) return labelKey;

  return nameKey;
};

const sortCoApplicantBasicFields = (fields: any[]): any[] => {
  if (!Array.isArray(fields)) return fields;
  return [...fields].sort((a, b) => {
    const indexA = CO_APPLICANT_BASIC_DISPLAY_ORDER.indexOf(
      getCoApplicantBasicFieldOrderKey(a),
    );
    const indexB = CO_APPLICANT_BASIC_DISPLAY_ORDER.indexOf(
      getCoApplicantBasicFieldOrderKey(b),
    );
    const orderA = indexA === -1 ? CO_APPLICANT_BASIC_DISPLAY_ORDER.length : indexA;
    const orderB = indexB === -1 ? CO_APPLICANT_BASIC_DISPLAY_ORDER.length : indexB;
    return orderA - orderB;
  });
};
// Type for co-applicant field
interface CoApplicantField {
  label?: string;
  name?: string;
  key?: string;
  value?: any;
  isVerified?: boolean;
}
// Type for co-applicant field value
type CoApplicantFieldValue = string | number | boolean | null | undefined;

const CO_APPLICANT_SOURCE_STEPS = [
  { horizontalIndex: 1, verticalIndex: 5 },
  { horizontalIndex: 3, verticalIndex: 1 },
  { horizontalIndex: 4, verticalIndex: 1 },
];

const tabLabels = ["Basic Details", "Address Details", "Employment Details"];

const SingleCoApplicantTabs: React.FC<SingleCoApplicantTabsProps> = ({
  keyNumber,
  coApplicantFields,
  coApplicantId,
  coApplicantNumber,
  title,
  isExpanded,
  onRemoveCoApplicant,
  relationDescByCode,
  maritalStatusDescByCode,
  resolveBirthCountryName,
  getCityOptionsForCountry,
  designationsVal,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [tabIndex, setTabIndex] = useState(0);
  const { fetchUserApplicationData, trackingId } = useReduxSelector(
    (state: ReduxRootState) => state.stepper
  );
  // Read synchronously (not in an effect) so a co-applicant just added via
  // "Add More" opens straight into edit mode on its very first render —
  // otherwise the read-only view flashes on screen for a frame before an
  // effect flips it over, which is exactly what looked broken to the user.
  const isAutoEditTarget = () =>
    typeof window !== "undefined" &&
    Boolean(trackingId) &&
    localStorage.getItem(`coAppAutoEditKey_${trackingId}`) ===
      String(coApplicantNumber);
  // For partner-started journeys, the active tab's content collapses behind a single
  // expand/collapse control near the Tabs, starting collapsed. Non-partner journeys keep
  // the content always visible (previous behavior) — isPanelExpanded stays null and falls
  // back to `true` for them.
  const [isPanelExpandedOverride, setIsPanelExpandedOverride] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [isEditingLocal, setIsEditing] = useState(isAutoEditTarget);
  // Only one co-applicant card may be in edit mode at a time — two mounted
  // FormEdits share the same redux form state and loop each other into
  // "Maximum update depth exceeded". Derived in render (not synced in an
  // effect) so the previous card drops back to view mode in the very same
  // render the next card opens its form.
  const summaryEditingCoApplicantKey = useSelector(
    (state: RootState) => state.stepper.summaryEditingCoApplicantKey
  );
  const isEditing =
    isEditingLocal &&
    (summaryEditingCoApplicantKey === null ||
      summaryEditingCoApplicantKey === String(coApplicantNumber));
  // Masks the moment between switching into edit mode and the edit form
  // actually rendering, so the customer sees a loader instead of a bare/
  // half-ready form flashing in. Cleared once FormEdit has had a render pass.
  const [isAutoEditPending, setIsAutoEditPending] = useState(isAutoEditTarget);
  const [pendingSaveRequest, setPendingSaveRequest] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const reduxHorizontalSteps = useSelector(
    (state: RootState) => state.stepper.horizontalSteps || []
  );
  const summaryApplicationEditActive = useSelector(
    (state: RootState) => state.stepper.summaryApplicationEditActive
  );
  const summarySaveInProgress = useSelector(
    (state: RootState) => state.stepper.summarySaveInProgress
  );
  const coApplicantBasicDetails = useSelector(
    (state: RootState) =>
      state.applicationLoanDetailsData.coApplicantBasicDetails?.[
        coApplicantId
      ] || []
  );
  const coApplicantAddressDetails = useSelector(
    (state: RootState) =>
      state.applicationLoanDetailsData.coApplicantAddressDetails?.[
        coApplicantId
      ] || {}
  );
  const coApplicantEmploymentDetails = useSelector(
    (state: RootState) =>
      state.applicationLoanDetailsData.coApplicantEmploymentDetails?.[
        coApplicantId
      ] || {}
  );
  
  const refreshCoApplicantData = async () => {
    if (
      !fetchUserApplicationData?.partnerStartedJourney ||
      !fetchUserApplicationData?.applicantId ||
      !fetchUserApplicationData?.applicationId
    ) {
      return;
    }

    setLoading(true);
    try {
      const [basicRes, addressRes, employmentRes] = await Promise.all([
        applicationDetailsServices.getCoApplicantBasicDetails(
          String(coApplicantId),
          trackingId || ""
        ),
        applicationDetailsServices.getApplicationLoanDetails(
          String(coApplicantId),
          trackingId || ""
        ),
        applicationDetailsServices.getEmploymentDetails(
          String(coApplicantId),
          trackingId || ""
        ),
      ]);

      const basicArr = basicRes?.data?.data || [];
      dispatch(
        setCoApplicantBasicDetails({
          coApplicantId,
          basicDetails: Array.isArray(basicArr) ? basicArr : [],
        })
      );

      const addressObj =
        addressRes?.data?.data?.["Application&LoanDetails"]?.AddressDetails;
      dispatch(
        setCoApplicantAddressDetails({
          coApplicantId,
          addressDetails: addressObj || {},
        })
      );

      dispatch(
        setCoApplicantEmploymentDetails({
          coApplicantId,
          employmentDetails: employmentRes?.data?.data?.EmploymentDetails || {},
        })
      );
    } catch (e) {
      dispatch(
        setCoApplicantBasicDetails({ coApplicantId, basicDetails: [] })
      );
      dispatch(
        setCoApplicantAddressDetails({ coApplicantId, addressDetails: {} })
      );
      dispatch(
        setCoApplicantEmploymentDetails({
          coApplicantId,
          employmentDetails: {},
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // Another co-applicant took over edit mode — reset this card's local edit
  // state too, so it stays in view mode once that other edit finishes.
  useEffect(() => {
    if (
      isEditingLocal &&
      summaryEditingCoApplicantKey !== null &&
      summaryEditingCoApplicantKey !== String(coApplicantNumber)
    ) {
      setIsEditing(false);
      setPendingSaveRequest(false);
    }
  }, [summaryEditingCoApplicantKey, isEditingLocal, coApplicantNumber]);

  useEffect(() => {
    if (pendingSaveRequest && !summaryApplicationEditActive) {
      void refreshCoApplicantData();
      setIsEditing(false);
      setPendingSaveRequest(false);
    }
  }, [pendingSaveRequest, summaryApplicationEditActive]);

  // A co-applicant just added via "Add More" (ApplicationDetails.tsx) opens
  // straight into edit mode (isEditing is already true from the lazy
  // useState initializer above, so there's no read-only-then-edit flash) and
  // scrolls into view — otherwise the customer has to scroll down and click
  // Edit themselves. This effect just does the one-time cleanup: clearing
  // the localStorage flag (namespaced by trackingId to match how
  // ApplicationDetails.tsx writes it) so it isn't reused on a later visit,
  // marking edit mode active, and revealing the form once it's had a render
  // pass instead of showing it mid-mount.
  useEffect(() => {
    if (!isAutoEditPending) return;
    const autoEditKeyName = `coAppAutoEditKey_${trackingId}`;
    if (
      typeof window === "undefined" ||
      !trackingId ||
      localStorage.getItem(autoEditKeyName) !== String(coApplicantNumber)
    ) {
      setIsAutoEditPending(false);
      return;
    }
    localStorage.removeItem(autoEditKeyName);
    dispatch(setSummaryApplicationEditActive(true));
    dispatch(setSummaryEditingCoApplicantKey(String(coApplicantNumber)));
    requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    // Give FormEdit a render pass (it mounts as soon as isEditing is read as
    // true, but its own master-data fetch effects still need to kick off)
    // before swapping the loader out for the live form.
    const revealTimer = setTimeout(() => setIsAutoEditPending(false), 500);
    return () => clearTimeout(revealTimer);
    
  }, []);

  const handleTabChange = async (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    // Mirrors CoApplicantDetailsSection.tsx/CoApplicantAdditionalDetails.tsx:
    // once a tab is open for editing, the customer can only move to another
    // tab by Saving (which validates the current one and advances tabIndex
    // itself, see handleInlineCoApplicantSectionSave) or Cancel — clicking a
    // tab header directly should never bypass required fields on the tab
    // currently being edited.
    if (summarySaveInProgress || isEditing) return;
    setTabIndex(newValue);
    // Only call APIs if partnerStartedJourney is true, applicantId !== null & applicationId !== null
    if (
      fetchUserApplicationData?.partnerStartedJourney &&
      fetchUserApplicationData?.applicantId &&
      fetchUserApplicationData?.applicationId
    ) {
      // if (newValue === 0 && (!coApplicantBasicDetails || coApplicantBasicDetails.length === 0)) {
      //   setLoading(true);
      //   try {
      //     const res = await applicationDetailsServices.getCoApplicantBasicDetails(
      //       String(coApplicantId),
      //       trackingId || ""
      //     );
      //     if (res && res.data) {
      //       const arr = res.data?.data || [];
      //       dispatch(
      //         setCoApplicantBasicDetails({
      //           coApplicantId,
      //           basicDetails: Array.isArray(arr) ? arr : [],
      //         })
      //       );
      //     }
      //   } catch (e) {
      //     dispatch(
      //       setCoApplicantBasicDetails({ coApplicantId, basicDetails: [] })
      //     );
      //   } finally {
      //     setLoading(false);
      //   }
      // }
      if (
        newValue === 1 &&
        (!coApplicantAddressDetails ||
          (!coApplicantAddressDetails.currentAddress &&
            !coApplicantAddressDetails.permanentAddress))
      ) {
        setLoading(true);
        try {
          const res =
            await applicationDetailsServices.getApplicationLoanDetails(
              String(coApplicantId),
              trackingId || ""
            );
          if (res && res.data) {
            const addressObj =
              res.data.data?.["Application&LoanDetails"]?.AddressDetails;
            // Store the full object for section-wise rendering
            dispatch(
              setCoApplicantAddressDetails({
                coApplicantId,
                addressDetails: addressObj || {},
              })
            );
          }
        } catch (e) {
          dispatch(
            setCoApplicantAddressDetails({ coApplicantId, addressDetails: {} })
          );
        } finally {
          setLoading(false);
        }
      }
      if (
        newValue === 2 &&
        (!coApplicantEmploymentDetails ||
          (!coApplicantEmploymentDetails.EmploymentDetails &&
            !coApplicantEmploymentDetails.CompanyAddressDetails))
      ) {
        setLoading(true);
        try {
          const res = await applicationDetailsServices.getEmploymentDetails(
            String(coApplicantId),
            trackingId || ""
          );
          if (res && res.data) {
            //   setEmploymentDetails(res.data.data?.EmploymentDetails || {});
            dispatch(
              setCoApplicantEmploymentDetails({
                coApplicantId,
                employmentDetails: res.data.data?.EmploymentDetails || {},
              })
            );
          }
        } catch (e) {
          dispatch(
            setCoApplicantEmploymentDetails({
              coApplicantId,
              employmentDetails: {},
            })
          );
        } finally {
          setLoading(false);
        }
      }
    }
  };

  // Fetch basic details when the section expands and Basic tab is active
  useEffect(() => {
    const fetchBasicIfNeeded = async () => {
      if (
        isExpanded &&
        tabIndex === 0 &&
        fetchUserApplicationData?.partnerStartedJourney &&
        fetchUserApplicationData?.applicantId &&
        fetchUserApplicationData?.applicationId &&
        (!coApplicantBasicDetails || coApplicantBasicDetails.length === 0)
      ) {
        setLoading(true);
        try {
          const res = await applicationDetailsServices.getCoApplicantBasicDetails(
            String(coApplicantId),
            trackingId || ""
          );
          if (res && res.data) {
            const arr = res.data?.data || [];
            dispatch(
              setCoApplicantBasicDetails({
                coApplicantId,
                basicDetails: Array.isArray(arr) ? arr : [],
              })
            );
          }
        } catch (e) {
          dispatch(
            setCoApplicantBasicDetails({ coApplicantId, basicDetails: [] })
          )
        } finally {
          setLoading(false);
        }
      }
    };

    fetchBasicIfNeeded();
  }, [isExpanded, tabIndex, fetchUserApplicationData, coApplicantId, trackingId]);

  const getFieldName = (field: any) =>
    String(field?.name || field?.key || "");

  // Merging config-derived fields (mergeMissingCoApplicants) alongside
  // already-loaded field values can produce two entries for the same
  // logical field (e.g. one with the real value, one blank placeholder) —
  // collapse them to a single entry per field identity, preferring
  // whichever has an actual value, so view mode never shows a field twice.
  const dedupeFields = (list: any[]): any[] => {
    if (!Array.isArray(list)) return list;
    const order: string[] = [];
    const byIdentity = new Map<string, any>();
    list.forEach((field: any) => {
      const identity = String(
        field?.name || field?.key || field?.label || "",
      )
        .trim()
        .toLowerCase();
      if (!identity) return;
      const existing = byIdentity.get(identity);
      if (!existing) {
        byIdentity.set(identity, field);
        order.push(identity);
        return;
      }
      const existingHasValue = existing?.value !== "" && existing?.value != null;
      const currentHasValue = field?.value !== "" && field?.value != null;
      if (!existingHasValue && currentHasValue) {
        byIdentity.set(identity, field);
      }
    });
    return order.map((identity) => byIdentity.get(identity));
  };
  const isCurrentAddressField = (name: string) =>
    /^addressCoApplicant\d+current/i.test(name) ||
    /^coApplicant\d+Current/i.test(name);
  const isPermanentAddressField = (name: string) =>
    /^addressCoApplicant\d+permanent/i.test(name) ||
    /^coApplicant\d+Permanent/i.test(name);
  const isAddressField = (name: string) =>
    /^addressCoApplicant\d+/i.test(name) ||
    /^coApplicant\d+(current|permanent)/i.test(name);
  const isEmploymentField = (name: string) =>
    /^employmentCoApplicant\d+/i.test(name) ||
    /^coApplicant\d+(employment|company|office|work)/i.test(name);

  const dedupedCoApplicantFields = dedupeFields(coApplicantFields);
  const dedupedCoApplicantBasicDetails = dedupeFields(coApplicantBasicDetails);
  const dedupedCurrentAddress = dedupeFields(coApplicantAddressDetails?.currentAddress);
  const dedupedPermanentAddress = dedupeFields(coApplicantAddressDetails?.permanentAddress);
  const dedupedEmploymentDetails = dedupeFields(
    coApplicantEmploymentDetails?.EmploymentDetails,
  );
  const dedupedCompanyAddressDetails = dedupeFields(
    coApplicantEmploymentDetails?.CompanyAddressDetails,
  );

  // Split address fields from coApplicantFields for Address Details tab
  const currentAddressFields = dedupedCoApplicantFields.filter((f) =>
    isCurrentAddressField(getFieldName(f)),
  );
  const permanentAddressFields = dedupedCoApplicantFields.filter((f) =>
    isPermanentAddressField(getFieldName(f)),
  );
  // Split employment fields for Employment Details tab
  const employmentFields = dedupedCoApplicantFields.filter((f) =>
    isEmploymentField(getFieldName(f)),
  );
  // Remove address and employment fields from basic details
  const basicFields = dedupedCoApplicantFields.filter((f) => {
    const name = getFieldName(f);
    return !isAddressField(name) && !isEmploymentField(name);
  });
  
  // Further split employment fields into Employment Details and Company Address
  // (fields carry `name` or `key`, so match on either)
  const companyAddressFields = employmentFields.filter((f) =>
    isCompanyAddressField(f)
  );
  const employmentDetailsFields = employmentFields.filter(
    (f) => !isCompanyAddressField(f)
  );
  const inferredCoApplicantNumber = coApplicantNumber;
  const sourceHorizontalSteps =
    Array.isArray(reduxHorizontalSteps) && reduxHorizontalSteps.length > 0
      ? reduxHorizontalSteps
      : fetchUserApplicationData?.horizontalSteps || [];
  const allEditableCoAppFields = CO_APPLICANT_SOURCE_STEPS.flatMap(
    ({ horizontalIndex, verticalIndex }) =>
      sourceHorizontalSteps?.[horizontalIndex]?.verticalSteps?.[verticalIndex]
        ?.fields || [],
  ).filter((field: any, idx: number, arr: any[]) => {
    const currentName = String(field?.name || "");
    if (!currentName) return false;
    return arr.findIndex((candidate: any) => String(candidate?.name || "") === currentName) === idx;
  });
  const editableFieldsForSelectedCoApp = allEditableCoAppFields.filter((field: any) => {
    const name = String(field?.name || "");
    if (!name) return false;
    return new RegExp(`CoApplicant${inferredCoApplicantNumber}`, "i").test(name);
  });
  const editFieldsByTab = editableFieldsForSelectedCoApp.filter((field: any) => {
    const name = String(field?.name || field?.key || "");
    if (tabIndex === 1) {
      return isAddressField(name);
    }
    if (tabIndex === 2) {
      return isEmploymentField(name);
    }
    return !isAddressField(name) && !isEmploymentField(name);
  });
  const handleInlineCoApplicantSectionSave = (savedTabIndex: number) => {
    if (savedTabIndex < 2) {
      setTabIndex(savedTabIndex + 1);
      setPendingSaveRequest(false);
      return true;
    }
    return false;
  };
  // Use co-applicant loan-details step for edit context so FormEdit
  // reliably loads co-app master dropdowns (relation, country, etc.).
  const editStepForTab = CO_APPLICANT_SOURCE_STEPS[0];
  const renderSectionHeading = (title: string, marginTop: number) => (
      <Grid item xs={12} sx={{ mt: marginTop, mb: 1 }}>
        <Box
          sx={{
            backgroundColor: "#edf6f6",
            borderLeft: "3px solid #00b3b5",
            borderRadius: "8px",
            px: 2,
            py: 1.25,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontSize: "15px", fontWeight: 600, color: "#6f6b7d" }}
          >
            {title}
          </Typography>
        </Box>
      </Grid>
    );
  // Employment items actually shown (API data when present, else form fields) —
  // used to decide whether the Company Address section applies
  const employmentItemsForCompanyCheck =
    Array.isArray(dedupedEmploymentDetails) && dedupedEmploymentDetails.length > 0
      ? dedupedEmploymentDetails
      : employmentDetailsFields;
  const showCompanyAddressFromFields = shouldShowCompanyAddress(
    employmentItemsForCompanyCheck,
  );
  const isPartnerJourney = Boolean(fetchUserApplicationData?.partnerStartedJourney);
  const isPanelExpanded = isPanelExpandedOverride ?? !isPartnerJourney;
   
  const handleTriggerConsentAuthorization = ({
    type,
    coApplicantIndex,
  }: { type: "applicant" | "coapplicant"; coApplicantIndex?: number | string }) => {
    // Add the consent mail API here; the button then opens the consent page.
    void type;
    void coApplicantIndex;
  };

  return (
    <>
      <Card ref={cardRef} sx={{ mb: 2, borderRadius: '10px', border: '1px solid #D7E3E3', boxShadow: 'none', overflow: 'hidden', position: 'relative' }}>
        {isAutoEditPending && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.92)',
            }}
          >
            <CircularProgress size={28} sx={{ color: '#00b3b5' }} />
          </Box>
        )}
        {fetchUserApplicationData?.partnerStartedJourney===false && fetchUserApplicationData?.status!=="Application Submitted" &&
          <Box sx={{ px: 2, py: 1.5, background: '#E6F6FA', borderBottom: '1px solid #D7E3E3', display: 'flex', alignItems: 'center', justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1F2937' }}>{title}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              
              {isEditing ? (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={summarySaveInProgress}
                    startIcon={
                      summarySaveInProgress ? (
                        <CircularProgress size={14} sx={{ color: "#fff" }} />
                      ) : undefined
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      backgroundColor: "#00b3b5",
                      "&:hover": { backgroundColor: "#00b3b5", opacity: 0.9 },
                      "&.Mui-disabled": {
                        backgroundColor: "#00b3b5",
                        color: "#fff",
                        opacity: 0.5,
                      },
                    }}
                    onClick={() => {
                      if (summarySaveInProgress) return;
                      setPendingSaveRequest(true);
                      dispatch(requestSummaryFormSave(`coApplicant${coApplicantNumber}`));
                    }}
                  >
                    {summarySaveInProgress ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={summarySaveInProgress}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      color: "#0EA5E9",
                      borderColor: "#0EA5E9",
                      "&:hover": { borderColor: "#0EA5E9", backgroundColor: "rgba(14, 165, 233, 0.06)" },
                      "&.Mui-disabled": {
                        color: "#0EA5E9",
                        borderColor: "#0EA5E9",
                        opacity: 0.5,
                      },
                    }}
                    onClick={() => {
                      if (summarySaveInProgress) return;
                      setIsEditing(false);
                      setPendingSaveRequest(false);
                      dispatch(setSummaryApplicationEditActive(false));
                      // Re-sync instead of leaving stale/empty local state in
                      // view mode — earlier tabs (e.g. Basic Details) may
                      // already be persisted from the inline save-and-advance
                      // flow even though this tab's edits are being discarded.
                      //
                      // refreshCoApplicantData() only calls its APIs for
                      // partner-started journeys (it's a no-op otherwise, same
                      // as every other fetch in this file). For every journey,
                      // toggling summarySaveInProgress true -> false mirrors a
                      // completed save and makes ApplicationDetails.tsx's own
                      // watcher (the thing that actually refreshes the
                      // `coApplicantFields` prop this component falls back to)
                      // refetch the full application — without going through
                      // requestSummaryFormSave(), which would re-run FormEdit's
                      // validate + submit pipeline on a plain Cancel.
                      dispatch(setSummarySaveInProgress(true));
                      Promise.resolve(refreshCoApplicantData()).finally(() => {
                        dispatch(setSummarySaveInProgress(false));
                      });
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* <ConsentAuthorizationButton
                    type="coapplicant"
                    coApplicantIndex={coApplicantNumber}
                    onClick={handleTriggerConsentAuthorization}
                  /> */}
                  <Button
                    size="small"
                    variant="text"
                    sx={{ textTransform: "none", fontWeight: 600, color: "#00b3b5" }}
                    onClick={() => {
                      setIsEditing(true);
                      setPendingSaveRequest(false);
                      dispatch(setSummaryApplicationEditActive(true));
                      dispatch(setSummaryEditingCoApplicantKey(String(coApplicantNumber)));
                    }}
                  >
                    <EditIcon sx={{ fontSize: "18px" }} />&nbsp; Edit
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        }
        {(isPartnerJourney || fetchUserApplicationData?.status==="Application Submitted") && (
          <Box
            onClick={() => isPartnerJourney? setIsPanelExpandedOverride(!isPanelExpanded):undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%", // Ensures it stretches full width so space-between works
              cursor: "pointer",
              color: "#616161",
              mb: isXsOrSm ? 0 : 2,
              py: 1, // Adds comfortable vertical padding
              px: 0.5,
              p: isXsOrSm ? 1 : 2,
              pt: isXsOrSm ? 0 : 2,
              borderBottom:fetchUserApplicationData?.status==="Application Submitted"?"1px solid lightgray":"",
            }}
          >
            {/* Left-aligned title */}
            <Typography variant="subtitle1" fontWeight="600">
              {`Co-Applicant ${keyNumber+1}`}
            </Typography>

            {/* Right-aligned icon */}
            {isPartnerJourney && 
            <IconButton size="small" sx={{ color: "inherit", p: 0 }}>
              { isPanelExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>}
          </Box>
        )}

      <Collapse in={isPanelExpanded} timeout="auto" unmountOnExit>
          
        <Box sx={{ 
          px: isXsOrSm ? 1 : 2,
          py: isXsOrSm ? 0 : 2,
          }}>
          <Box sx={{ display: "flex", alignItems: "center", }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant={isXsOrSm ? "scrollable" : "standard"}
              {...(isXsOrSm && {
                scrollButtons: "auto",
                allowScrollButtonsMobile: false,
              })}
              sx={{
                mb: isXsOrSm ? 1 : 2,
                flexGrow: 1,
                "& .MuiTabs-indicator": {
                  backgroundColor: "#00b3b5",
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#616161",
                  "&.Mui-selected": {
                    color: "#00b3b5",
                  },
                },
              }}
            >
              {tabLabels?.map((label, i) => (
                <Tab key={label} label={label} disabled={isEditing && tabIndex !== i} />
              ))}
            </Tabs>
            {/* Tab Panels */}
          
          </Box>
          {tabIndex === 0 && (
            isEditing ? (
              <>
                {/* {renderSectionHeading("Basic Details", 2)} */}
                <FormEdit
                  fields={editFieldsByTab}
                  pageType={"SUMMARY"}
                  activeHorizontalIndex={editStepForTab.horizontalIndex}
                  activeVerticalIndex={editStepForTab.verticalIndex}
                  forceInlineFieldRender={true}
                  onSummaryCoApplicantSectionSave={handleInlineCoApplicantSectionSave}
                  summarySaveTargetKey={`coApplicant${coApplicantNumber}`}
                />
              </>
            ) : (
              <>
                {/* {renderSectionHeading("Basic Details",2)} */}
                <Grid container spacing={2}>
                  {loading ? (
                    tabContentSkeleton()
                  ) : (
                    sortCoApplicantBasicFields(
                      dedupedCoApplicantBasicDetails && dedupedCoApplicantBasicDetails.length > 0
                        ? dedupedCoApplicantBasicDetails
                        : basicFields
                    )
                      ?.filter((field: CoApplicantField) => {
                        if (/^coApplicant\d+IsPanVerified$/.test(field?.name || field?.key || '')) return false;
                        if (/^coApplicant\d+IsMobileVerified$/.test(field?.name || field?.key || '')) return false;
                        if (/^coApplicant\d+IsEmailVerified$/.test(field?.name || field?.key || '')) return false;
                        return true;
                      })
                      .map((field: CoApplicantField, idx: number) => {
                        let isVerified = false;
                        const effectiveBasicFields = (
                          dedupedCoApplicantBasicDetails && dedupedCoApplicantBasicDetails.length > 0
                            ? dedupedCoApplicantBasicDetails
                            : basicFields
                        ) as CoApplicantField[];
                        const fieldName = field?.name || field?.key || '';
                        const isCoApplicantPhone =
                          /^basicCoApplicant\d+PhoneNumber$/.test(fieldName) ||
                          /^coApplicant\d+Phone_number$/.test(fieldName);
                        if (isCoApplicantPhone) {
                          const verifiedField = effectiveBasicFields?.find((f) => {
                            const name = f?.name || f?.key || '';
                            return ((/^basicCoApplicant\d+IsMobileVerified$/.test(name) ||
                              /^coApplicant\d+IsMobileVerified$/.test(name)) && f?.value === true);
                          });
                          if (verifiedField) isVerified = true;
                        }
                        const isCoApplicantEmail =
                          /^basicCoApplicant\d+Email$/.test(fieldName) ||
                          /^coApplicant\d+Email$/.test(fieldName);
                        if (isCoApplicantEmail) {
                          const verifiedField = effectiveBasicFields?.find((f) => {
                            const name = f?.name || f?.key || '';
                            return ((/^basicCoApplicant\d+IsEmailVerified$/.test(name) ||
                              /^coApplicant\d+IsEmailVerified$/.test(name)) && f?.value === true);
                          });
                          if (verifiedField) isVerified = true;
                        }
                        const isCoApplicantPan =
                          /^basicCoApplicant\d+PanNumber$/.test(fieldName) ||
                          /^coApplicant\d+PanNumber$/.test(fieldName);
                        if (isCoApplicantPan) {
                          const verifiedField: CoApplicantField | undefined = effectiveBasicFields?.find((f: CoApplicantField) => {
                            const name = f?.name || f?.key || '';
                            return ((/^basicCoApplicant\d+IsPanVerified$/.test(name) ||
                              /^coApplicant\d+IsPanVerified$/.test(name)) && f?.value === true);
                          });
                          if (verifiedField) isVerified = true;
                        }
                        const skipNames = ['aadhaar'];
                        if (
                          field?.name &&
                          skipNames.some((skip) => field.name && field.name.toLowerCase().includes(skip)) &&
                          /(coApplicant\d+|basicCoApplicant\d+)/i.test(field.name)
                        ) {
                          return null;
                        }
                        const isDobField =
                          /^basicCoApplicant\d+dob$/.test(fieldName) ||
                          /^coApplicant\d+Dob$/.test(fieldName);
                        const payparty =
                          /^basicCoApplicant\d+PayParty$/.test(fieldName) ||
                          /^coApplicant\d+PayParty$/.test(fieldName);
                        const isRelationField =
                          !/OtherRelationWithStudent/i.test(fieldName) &&
                          (/^basicCoApplicant\d+RelationWithStudent$/.test(fieldName) ||
                            /^coApplicant\d+RelationWithStudent$/.test(fieldName));
                        const relationDesc =
                          isRelationField && field?.value != null
                            ? relationDescByCode?.[String(field.value).trim().toUpperCase()]
                            : undefined;
                        const isMaritalStatusField =
                          /^basicCoApplicant\d+MaritalStatus$/.test(fieldName) ||
                          /^coApplicant\d+MaritalStatus$/.test(fieldName);
                        const maritalStatusDesc =
                          isMaritalStatusField && field?.value != null
                            ? maritalStatusDescByCode?.[String(field.value).trim().toUpperCase()]
                            : undefined;
                        // City of Birth is saved as a short city code; resolve it to the
                        // full city name using the cities master for whichever country
                        // this co-applicant's Country of Birth points to.
                        const isCityOfBirthField = fieldName.toLowerCase().includes("cityofbirth");
                        let cityOfBirthDesc: string | undefined;
                        if (
                          isCityOfBirthField &&
                          field?.value != null &&
                          field?.value !== "" &&
                          resolveBirthCountryName &&
                          getCityOptionsForCountry
                        ) {
                          const countryField = effectiveBasicFields?.find((f) =>
                            String(f?.name || f?.key || "").toLowerCase().includes("countryofbirth")
                          );
                          const countryValue = countryField?.value;
                          if (countryValue) {
                            const countryLabel = resolveBirthCountryName(countryValue);
                            const cityOptions = getCityOptionsForCountry(String(countryLabel));
                            const cityMatch = cityOptions.find(
                              (opt) =>
                                String(opt.value).toLowerCase() === String(field.value).toLowerCase() ||
                                String(opt.label).toLowerCase() === String(field.value).toLowerCase()
                            );
                            cityOfBirthDesc = cityMatch?.label;
                          }
                        }
                        let displayValue = field?.value;
                        if (isDobField) displayValue = formatDOB(field?.value);
                        else if (isRelationField) displayValue = relationDesc ?? field?.value;
                        else if (isMaritalStatusField) displayValue = maritalStatusDesc ?? field?.value;
                        else if (isCityOfBirthField) displayValue = cityOfBirthDesc ?? field?.value;
                        else if(payparty) displayValue = field?.value === true ||  field?.value === "Yes"? "Yes" : "No";
                        return (
                          field?.label!="Company Name" &&
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={field?.name || field?.key || idx}
                          >
                            <DetailRow
                              item={{
                                ...field,
                                label: field?.label ?? '',
                                value: displayValue,
                                isVerified,
                              }}
                            />
                          </Grid>
                        );
                      })
                  )}
                </Grid>
              </>
            )
          )}
          {tabIndex === 1 && (
            isEditing ? (
              <>
                {renderSectionHeading("Address Details", 2)}
                <FormEdit
                  fields={editFieldsByTab}
                  pageType={"SUMMARY"}
                  activeHorizontalIndex={editStepForTab.horizontalIndex}
                  activeVerticalIndex={editStepForTab.verticalIndex}
                  forceInlineFieldRender={true}
                  onSummaryCoApplicantSectionSave={handleInlineCoApplicantSectionSave}
                  summarySaveTargetKey={`coApplicant${coApplicantNumber}`}
                />
              </>
            ) : (
              <>
              <Box sx={{ mb: 2, mt: 1 }}>
                {/* <Typography
                  variant="subtitle2"
                  sx={{ fontSize: '15px', fontWeight: 600, color: '#6f6b7d' }}
                >
                  Address Details
                </Typography> */}
              </Box>
              <Grid container spacing={2}>
                {loading ? (
                  tabContentSkeleton()
                ) : (
                  <>
                    {/* Current Address Section */}
                    {(dedupedCurrentAddress &&
                      dedupedCurrentAddress.length > 0) ||
                    currentAddressFields.length > 0 ? (
                      <>
                        {renderSectionHeading("Current Address", isXsOrSm ? -1 : 2)}
                        {(dedupedCurrentAddress &&
                        dedupedCurrentAddress.length > 0
                          ? dedupedCurrentAddress
                          : currentAddressFields
                        ).map((field: any, subIndex: number) => {
                          let label = field.label || '';
                          label = label.replace(/^current\s*/i, '');
                          label = label.replace(/(\D)(\d+)/g, '$1 $2');
                          label = label.replace(/([a-z])([A-Z])/g, '$1 $2');
                          label = label
                            .split(' ')
                            .map((w: string) =>
                              w.length ? w[0].toUpperCase() + w.slice(1) : ''
                            )
                            .join(' ');
                          return (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              md={3}
                              key={`current-${subIndex}`}
                              sx={{ ml: isXsOrSm ? 2 : 0 }}
                            >
                              <DetailRow
                                item={{
                                  label,
                                  value: field.value,
                                  isVerified: false,
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </>
                    ) : (
                      <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
                        <Typography color="text.secondary" fontSize={14}>
                          No address details available.
                        </Typography>
                      </Grid>
                    )}
                    {/* Permanent Address Section */}
                    {(dedupedPermanentAddress &&
                      dedupedPermanentAddress.length > 0) ||
                    permanentAddressFields.length > 0 ? (
                      <>
                        {renderSectionHeading("Permanent Address",2)}
                        {(dedupedPermanentAddress &&
                        dedupedPermanentAddress.length > 0
                          ? dedupedPermanentAddress
                          : permanentAddressFields
                        ).map((field: any, subIndex: number) => {
                          let label = field.label || '';
                          label = label.replace(/^Permanent\s*/i, '');
                          label = label.replace(/(\D)(\d+)/g, '$1 $2');
                          label = label.replace(/([a-z])([A-Z])/g, '$1 $2');
                          label = label
                            .split(' ')
                            .map((w: string) =>
                              w.length ? w[0].toUpperCase() + w.slice(1) : ''
                            )
                            .join(' ');
                          return (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              md={3}
                              key={`permanent-${subIndex}`}
                              sx={{ ml: isXsOrSm ? 2 : 0 }}
                            >
                              <DetailRow
                                item={{
                                  label,
                                  value: field.value,
                                  isVerified: false,
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </>
                    ) : null}
                  </>
                )}
              </Grid>
            </>
            )
          )}
          {tabIndex === 2 && (
            isEditing ? (
              <>
                {/* {renderSectionHeading("Employment Details", 2)} */}
                <FormEdit
                  fields={editFieldsByTab}
                  pageType={"SUMMARY"}
                  activeHorizontalIndex={editStepForTab.horizontalIndex}
                  activeVerticalIndex={editStepForTab.verticalIndex}
                  forceInlineFieldRender={true}
                  onSummaryCoApplicantSectionSave={handleInlineCoApplicantSectionSave}
                  summarySaveTargetKey={`coApplicant${coApplicantNumber}`}
                />
              </>
            ) : (
              <>
              <Grid container spacing={2}>
                {loading ? (
                  tabContentSkeleton()
                ) : (
                  <>
                    {/* Employment Details Section */}
                    {employmentDetailsFields.length > 0 ? (
                      <>
                      {/* {renderSectionHeading("Employment Details", 2)} */}
                      {(() => {
                        // 1. Determine base source list
                        const baseEmploymentFields =
                          dedupedEmploymentDetails &&
                          Array.isArray(dedupedEmploymentDetails) &&
                          dedupedEmploymentDetails.length > 0
                            ? dedupedEmploymentDetails
                            : employmentDetailsFields;
                        // Company Name belongs to Employment Details (API data may send it
                        // inside CompanyAddressDetails)
                        const companyNameFromAddress = (dedupedCompanyAddressDetails || []).filter(
                          isCompanyNameField,
                        );
                        const rawFields = baseEmploymentFields.some(isCompanyNameField)
                          ? baseEmploymentFields
                          : [...baseEmploymentFields, ...companyNameFromAddress];
                         
                        // 2. Check if "Is Employed" field is set to false
                        // (name can be isEmployed or employmentCoApplicantNisEmployed)
                        const isIsEmployedField = (f: any) =>
                          String(f?.name || f?.key || "").toLowerCase().endsWith("isemployed") ||
                          f?.label?.toLowerCase() === "is employed";
                        const isEmployedField = rawFields.find(isIsEmployedField);
                        const isNotEmployed =
                          isEmployedField?.value === false ||
                          String(isEmployedField?.value ?? "").toLowerCase() === "no" ||
                          String(isEmployedField?.value ?? "").toLowerCase() === "false";

                        // 3. If NOT employed, keep ONLY the "Is Employed" field; otherwise, keep all valid fields
                        const filteredFields = rawFields.filter((field: any) => {
                          // Basic valid check
                          const isValidValue =
                            (field?.value !== "" && field?.value !== null && field?.value !== undefined) ||
                            field?.value === false;

                          if (!isValidValue) return false;

                          // IF Not Employed, hide all fields EXCEPT "Is Employed"
                          if (isNotEmployed) {
                            return isIsEmployedField(field);
                          }

                          return true;
                        });

                        // Designation is stored as a code (e.g. "ACR") — resolve it to
                        // its full label (e.g. "Area Credit Manager") the same way the
                        // main applicant's Employment Details tab already does.
                        const resolveDesignationValue = (field: any) => {
                          if (!isDesignationField(field)) return field.value;
                          const match = (designationsVal || []).find(
                            (d: any) => d?.genDesignation === field.value || d?.genDesgDesc === field.value,
                          );
                          return match?.genDesgDesc ?? field.value;
                        };

                        return filteredFields.map((field: any, idx: number) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            key={`emp-${idx}`}
                            sx={{ ml: isXsOrSm ? 2 : 0 }}
                          >
                            <DetailRow
                              item={{
                                label: field.label,
                                value:
                                  field.value === false
                                    ? "No"
                                    : field.value === true
                                      ? "Yes"
                                      : resolveDesignationValue(field),
                                isVerified: false,
                              }}
                            />
                          </Grid>
                        ));
                      })()}
                      </>
                    ) : (
                      <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
                        <Typography color="text.secondary" fontSize={14}>
                          No employment details available.
                        </Typography>
                      </Grid>
                    )}
                    {/* Company Address Section */}
                    {(dedupedCompanyAddressDetails &&
                      dedupedCompanyAddressDetails?.length > 0) ||
                    (showCompanyAddressFromFields && companyAddressFields.length > 0) ? (
                      <>
                        {(() => {
                          // 1. Determine base source list — without Company Name (shown in
                          // Employment Details), ordered Address Line 1, 2, 3, Landmark,
                          // Pincode, City, State, Country
                          const rawFields = sortCompanyAddressFields(
                            (dedupedCompanyAddressDetails &&
                            dedupedCompanyAddressDetails?.length > 0
                              ? dedupedCompanyAddressDetails
                              : companyAddressFields
                            ).filter((field: any) => !isCompanyNameField(field)),
                          );

                          // 2. Filter out items where value is blank, null, or undefined (allows boolean false)
                          const validFields = rawFields?.filter((field: any) => {
                            return (
                              (field?.value !== "" &&
                                field?.value !== null &&
                                field?.value !== undefined) ||
                              field?.value === false
                            );
                          }) || [];

                          // 3. If no valid fields exist, do not render the section heading or grid
                          if (validFields.length === 0) {
                            return null;
                          }

                          // 4. Render heading once and map only valid fields
                          return (
                            <>
                              {renderSectionHeading("Company Address", 2)}
                              {validFields.map((field: any, idx: number) => (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={3}
                                  key={`comp-${idx}`}
                                  sx={{ ml: isXsOrSm ? 2 : 0 }}
                                >
                                  <DetailRow
                                    item={{
                                      label: getCompanyAddressLabel(field.label),
                                      value: field.value === false ? "No" : field.value === true ? "Yes" : field.value,
                                      isVerified: false,
                                    }}
                                  />
                                </Grid>
                              ))}
                            </>
                          );
                        })()}
                      </>
                    ) : null}
                  </>
                )}
              </Grid>
            </>
            )
          )}
         
        </Box>
       </Collapse>
      </Card>
      {coApplicantNumber > 1 &&  fetchUserApplicationData?.partnerStartedJourney===false && fetchUserApplicationData?.status!=="Application Submitted" && (
        <Button
          size="small"
          variant="text"
          disabled={isRemoving}
          sx={{ textTransform: "none", fontWeight: 600, color: "#d32f2f" }}
          onClick={async () => {
            if (!onRemoveCoApplicant) return;
            setIsRemoving(true);
            try {
              await onRemoveCoApplicant(coApplicantNumber);
            } finally {
              setIsRemoving(false);
            }
          }}
        >
          <img src={RemoveIcon} alt="arrow-narrow-right" />
          <Typography
            variant="subtitle2"
            sx={{
              ml: 1,
              fontWeight: 600,
              color: "#FF4C51",
              fontSize: "13px",
            }}
          >
            {isRemoving ? "Removing..." : "Remove"}
          </Typography>
          
        </Button>
      )}
    </>
  );
};

interface CoApplicantTabsProps {
  coApplicant: any;
  pageTypeSummary?: string;
  onRemoveCoApplicant?: (coApplicantNumber: number) => Promise<void> | void;
  designationsVal?: any;
}

const CoApplicantTabs: React.FC<CoApplicantTabsProps> = ({
  coApplicant,
  pageTypeSummary,
  onRemoveCoApplicant,
  designationsVal,
}) => {
  const { fetchUserApplicationData } = useReduxSelector(
    (state: ReduxRootState) => state.stepper
  );

  const { countryOptions, fetchCitiesByCountryState, getCityOptionsForCountry } =
    useApplicantMasterData();

  // Country of Birth is saved as a country code, but the cities-by-country
  // API is keyed by the full country name — resolve internally for that lookup.
  const resolveBirthCountryName = (rawCountryValue: any): string => {
    const raw = String(rawCountryValue ?? "");
    if (!raw) return raw;
    const match = countryOptions.find(
      (opt) =>
        String((opt as any)?.countryId ?? "").toLowerCase() === raw.toLowerCase() ||
        String(opt.value).toLowerCase() === raw.toLowerCase() ||
        String(opt.label).toLowerCase() === raw.toLowerCase()
    );
    return match?.label ?? raw;
  };

  const [relationDescByCode, setRelationDescByCode] = useState<Record<string, string>>({});

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await losServices.relationMaster();
        const list = res?.data?.data;
        if (!isMounted || !Array.isArray(list)) return;
        const map: Record<string, string> = {};
        list.forEach((item: any) => {
          const code = item?.codeInPennant;
          const desc = item?.descInPennant;
          if (code != null && desc != null) {
            map[String(code).trim().toUpperCase()] = desc;
          }
        });
        setRelationDescByCode(map);
      } catch (error) {
        console.log(error);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const [maritalStatusDescByCode, setMaritalStatusDescByCode] = useState<Record<string, string>>({});

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await dropdownService.getMaritalStatusMaster();
        const list = (res?.data || []) as any[];
        if (!isMounted || !Array.isArray(list)) return;
        const map: Record<string, string> = {};
        list.forEach((item: any) => {
          const code = item?.maritalStatusCode ?? item?.code ?? item?.value;
          const desc = item?.maritalStatusName ?? item?.maritalStatusDesc ?? item?.desc ?? item?.label ?? item?.name;
          if (code != null && desc != null) {
            map[String(code).trim().toUpperCase()] = String(desc);
          }
        });
        setMaritalStatusDescByCode(map);
      } catch (error) {
        console.log(error);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);
// Normalize object shape `{ coapplicant1: {...}, coapplicant2: {...} }`
  // into the array format expected by this component
  const transformCoApplicantObjectToArray = (input: any) => {
    if (!input || Array.isArray(input) || typeof input !== "object") return input;
    const entries = Object.entries(input).filter(([k]) => /^coapplicant\d+$/i.test(k));
    if (entries.length === 0) return input;
    return entries
      .sort(([a], [b]) => {
        const numA = parseInt(String(a).replace(/[^\d]/g, ""), 10);
        const numB = parseInt(String(b).replace(/[^\d]/g, ""), 10);
        return numA - numB;
      })
      .map(([key, value]: [string, any]) => {
        const fields: any[] = Object.entries(value || {})
          .filter(([fieldKey]) => fieldKey !== "coApplicantId")
          .map(([fieldKey, fieldValue]) => {
            const label = String(fieldKey)
              .replace(/^basicCoApplicant\d+|^addressCoApplicant\d+|^employmentCoApplicant\d+/i, "")
              .replace(/([A-Z])/g, " $1")
              .replace(/_/g, " ")
              .replace(/^./, (s) => s.toUpperCase())
              .trim();
            return {
              key: fieldKey,
              name: fieldKey,
              label,
              value: fieldValue as any,
            };
          });
        return {
          [key]: fields,
          [`${key}Id`]: value?.coApplicantId,
        };
      });
  };

  // Helper to map co-applicant fields from multiple vertical steps to the required format
  function mapCoApplicantDetailsFromVerticalSteps(verticalSteps: any[]) {
    const mergedFields = (Array.isArray(verticalSteps) ? verticalSteps : [])
      .flatMap((verticalStep: any) => verticalStep?.fields || [])
      .filter((fieldObj: any, index: number, allFields: any[]) => {
        const currentKey = Object.keys(fieldObj || {})[0];
        return (
          allFields.findIndex((candidate: any) => {
            const candidateKey = Object.keys(candidate || {})[0];
            return candidateKey === currentKey;
          }) === index
        );
      });

    if (mergedFields.length === 0) return [];
    // Group fields by co-applicant number
    const coAppMap: { [key: string]: any[] } = {};
    const coAppIdMap: { [key: string]: any } = {};
    mergedFields.forEach((fieldObj: any) => {
      const key: string = Object.keys(fieldObj)?.[0] as string;
      let value: CoApplicantFieldValue = fieldObj?.[key];
      // Try to extract co-applicant number from key (e.g., coApplicant1Name, coApplicant2PanNumber, etc.)
      const match: RegExpMatchArray | null =
        key.match(/CoApplicant(\d+)/i) || key.match(/coApplicant(\d+)/i);
      if (!match) return;
      const coAppNum = match[1];
      const coAppKey = `coapplicant${coAppNum}`;
      if (!coAppMap[coAppKey]) coAppMap[coAppKey] = [];

      // Normalize label and key as per requirements
      // Helper to remove known prefixes and format label
      function stripPrefixAndFormat(str: string) {
        // Remove known prefixes
        const prefixPattern =
          /^(basicCoApplicant\d+|addressCoApplicant\d+|employmentCoApplicant\d+)/i;
        const noPrefix = str.replace(prefixPattern, "");
        // Convert camelCase or snake_case to Title Case
        return noPrefix
          .replace(/([A-Z])/g, " $1")
          .replace(/_/g, " ")
          .replace(/^./, (s) => s.toUpperCase())
          .trim();
      }
      let label = fieldObj?.label || stripPrefixAndFormat(key);
      if (/OtherRelationWithStudent/i.test(key)) label = "Specify Relationship";
      else if (/RelationWithStudent/i.test(key)) label = "Relationship with Student";
      else if (/FirstName/i.test(key)) label = "Co-Applicant First Name";
      else if (/LastName/i.test(key)) label = "Co-Applicant Last Name";
      else if (/PanNumber/i.test(key)) label = "PAN Number";
      else if (/IsPanVerified/i.test(key)) label = "PAN Verified";
      else if (/Email$/i.test(key) && !/IsEmailVerified/i.test(key))
        label = "Email ID";
      else if (/IsEmailVerified/i.test(key)) label = "Email Verified";
      else if (/Phone_number/i.test(key)) label = "Mobile Number";
      else if (/IsMobileVerified/i.test(key)) label = "Mobile Verified";

      // Use correct name property for output
      let name = key;
      if (/RelationWithStudent/i.test(key))
        name = `coApplicant${coAppNum}RelationWithStudent`;
      // Was a single `/Name/i` match renaming EVERY "...Name" key (FirstName,
      // LastName, CompanyName, ...) to the exact same `coApplicant<N>Name` —
      // dedupeFields (further downstream) identifies fields by `.name`, so
      // First Name and Last Name collapsed into one entry and Last Name
      // silently disappeared from the UI. Match FirstName/LastName precisely
      // so each keeps its own distinct name.
      else if (/FirstName/i.test(key)) name = `coApplicant${coAppNum}FirstName`;
      else if (/LastName/i.test(key)) name = `coApplicant${coAppNum}LastName`;
      else if (/PanNumber/i.test(key)) name = `coApplicant${coAppNum}PanNumber`;
      else if (/IsPanVerified/i.test(key))
        name = `coApplicant${coAppNum}IsPanVerified`;
      else if (/Email$/i.test(key) && !/IsEmailVerified/i.test(key))
        name = `coApplicant${coAppNum}Email`;
      else if (/IsEmailVerified/i.test(key))
        name = `coApplicant${coAppNum}IsEmailVerified`;
      else if (/Phone_number/i.test(key))
        name = `coApplicant${coAppNum}Phone_number`;
      else if (/IsMobileVerified/i.test(key))
        name = `coApplicant${coAppNum}IsMobileVerified`;

      coAppMap[coAppKey].push({
        label,
        name,
        key,
        value,
      });

      // Optionally, extract co-applicant ID if present
      if (/Id$/.test(key)) {
        coAppIdMap[coAppKey] = value;
      }
    });

    // Build the final array, sorted by co-applicant number
    const grouped: any[] = Object.keys(coAppMap)
      .sort((a, b) => {
        // Extract numbers from keys like 'coapplicant1', 'coapplicant2', etc.
        const numA = parseInt(a.replace(/[^\d]/g, ""), 10);
        const numB = parseInt(b.replace(/[^\d]/g, ""), 10);
        return numA - numB;
      })
      .map((coAppKey) => ({
        [coAppKey]: coAppMap[coAppKey],
        [`${coAppKey}Id`]: coAppIdMap[coAppKey],
      }));

    return grouped;
  }

  let coApplicantToShow = coApplicant;
  if (
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    const coApplicantSourceVerticalSteps = CO_APPLICANT_SOURCE_STEPS.map(
      ({ horizontalIndex, verticalIndex }) =>
        fetchUserApplicationData?.horizontalSteps?.[horizontalIndex]?.verticalSteps?.[
          verticalIndex
        ],
    ).filter(Boolean);
    coApplicantToShow = mapCoApplicantDetailsFromVerticalSteps(
      coApplicantSourceVerticalSteps,
    );
  }
  
    const horizontalSteps = useSelector(
      (state: RootState) => state?.stepper?.horizontalSteps
    );
   const coApplicantSourceVerticalStepsone = CO_APPLICANT_SOURCE_STEPS.map(
      ({ horizontalIndex, verticalIndex }) =>
        horizontalSteps?.[horizontalIndex]?.verticalSteps?.[
          verticalIndex
        ],
    ).filter(Boolean);
    
    // If coApplicant is an object map (for partnerStartedJourney true), normalize to array
    coApplicantToShow = transformCoApplicantObjectToArray(coApplicantToShow);
    coApplicantToShow=mergeMissingCoApplicants(coApplicantToShow,coApplicantSourceVerticalStepsone)
  

  // City-of-birth values come back from the backend as short city codes.
  // Resolving a code to its full city name requires the city master list for
  // whichever country that co-applicant's Country of Birth points to, so
  // prefetch every distinct birth country referenced across co-applicants.
  useEffect(() => {
    if (!Array.isArray(coApplicantToShow) || countryOptions.length === 0) return;

    const birthCountries = new Set<string>();
    coApplicantToShow.forEach((coAppObj: any) => {
      const coAppKey = Object.keys(coAppObj || {}).find((k) =>
        k.startsWith("coapplicant")
      );
      const fields = coAppKey ? coAppObj[coAppKey] : [];
      if (!Array.isArray(fields)) return;
      const countryField = fields.find((f: any) =>
        String(f?.name || f?.key || "").toLowerCase().includes("countryofbirth")
      );
      if (countryField?.value) {
        birthCountries.add(resolveBirthCountryName(countryField.value));
      }
    });

    birthCountries.forEach((countryLabel) => {
      if (getCityOptionsForCountry(countryLabel).length === 0) {
        fetchCitiesByCountryState(countryLabel);
      }
    });
  }, [coApplicantToShow, countryOptions, fetchCitiesByCountryState, getCityOptionsForCountry]);

  if (!Array.isArray(coApplicantToShow)) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }
 
  return (
    <>
      {coApplicantToShow?.length > 0 ? (
        coApplicantToShow?.map((coAppObj: any, idx: any) => {
          
          // Find the key like 'coapplicant1', 'coapplicant2', etc.
          const coAppKey = Object.keys(coAppObj).find((k) =>
            k.startsWith("coapplicant")
          );
          const coAppFields = coAppKey ? coAppObj[coAppKey] : [];
          const coAppId = coAppObj[`${coAppKey}Id`];
          
          const coAppNumFromKey = coAppKey
            ? Number(String(coAppKey).replace(/[^\d]/g, "")) || idx + 1
            : idx + 1;

          if (!coAppFields || coAppFields.length === 0) {
            return (
              <Grid item xs={12} key={coAppId || idx}>
                <Typography variant="body2" color="textSecondary">
                  No details available.
                </Typography>
              </Grid>
            );
          }

          return (
            <SingleCoApplicantTabs
              keyNumber={idx}
              coApplicantFields={coAppFields}
              coApplicantId={coAppId}
              coApplicantNumber={coAppNumFromKey}
              title={`Co-Applicant ${idx+1}`}
              isExpanded={true}
              onRemoveCoApplicant={onRemoveCoApplicant}
              relationDescByCode={relationDescByCode}
              maritalStatusDescByCode={maritalStatusDescByCode}
              resolveBirthCountryName={resolveBirthCountryName}
              getCityOptionsForCountry={getCityOptionsForCountry}
              designationsVal={designationsVal}
            />
          );
        })
      ) : (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            No details available.
          </Typography>
        </Grid>
      )}
    </>
  );
};

export default CoApplicantTabs;
